import React, { useState } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Alert, AlertDescription } from "../components/ui/alert";
import apiClient from "../services/api";
import SEO from "../components/SEO";

const DonatePage = () => {
  const [formData, setFormData] = useState({
    amount: "",
    donorName: "",
    donorEmail: "",
    message: "",
    anonymous: false
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: "", text: "" });

  const presetAmounts = [10, 25, 50, 100, 250];

  const handleChange = (e) => {
    const value = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setFormData({
      ...formData,
      [e.target.name]: value
    });
  };

  const handlePresetAmount = (amount) => {
    setFormData({
      ...formData,
      amount: amount.toString()
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setMessage({ type: "", text: "" });

    try {
      const donationData = {
        amount: parseFloat(formData.amount),
        donor_name: formData.anonymous ? null : formData.donorName,
        donor_email: formData.anonymous ? null : formData.donorEmail,
        message: formData.message || null,
        anonymous: formData.anonymous
      };

      const response = await apiClient.post("/donations/create", donationData);
      
      // If we got a redirect URL, redirect to GoCardless
      if (response.data.redirect_url) {
        setMessage({ 
          type: "success", 
          text: `Redirecting to secure payment page...` 
        });
        // Wait a moment then redirect
        setTimeout(() => {
          window.location.href = response.data.redirect_url;
        }, 1500);
      } else {
        // No redirect URL means payment setup failed
        setMessage({ 
          type: "error", 
          text: response.data.message || "Payment setup failed. Please try again or contact us." 
        });
      }
      
    } catch (error) {
      setMessage({ 
        type: "error", 
        text: error.response?.data?.detail || "Donation failed. Please try again." 
      });
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title="Donate"
        description="Support horses in need across Scotland. Your donation helps provide care, rehabilitation, and secure futures for horses who can no longer be cared for by their owners. Every contribution makes a difference."
        keywords="donate to horse sanctuary, support equine welfare Scotland, horse charity donation, help horses Scotland"
        canonical="https://thegrazingtrust.org/donate"
      />
      <div className="bg-emerald-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Support Our Mission
          </h1>
          <p className="text-lg md:text-xl max-w-2xl mx-auto">
            Your donation helps us provide care, rehabilitation, and a second chance 
            for horses in need across Scotland.
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <Card>
            <CardHeader>
              <CardTitle>£25</CardTitle>
              <CardDescription>Feeds a horse for a week</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>£50</CardTitle>
              <CardDescription>Provides farrier care</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>£100</CardTitle>
              <CardDescription>Covers veterinary treatment</CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle>£250</CardTitle>
              <CardDescription>Supports a rescue operation</CardDescription>
            </CardHeader>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Make a Donation</CardTitle>
            <CardDescription>
              Every contribution makes a difference in a horse's life
            </CardDescription>
          </CardHeader>
          <CardContent>
            {message.text && (
              <Alert variant={message.type === "error" ? "destructive" : "default"} className="mb-6">
                <AlertDescription>{message.text}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label className="mb-3 block">Select an amount</Label>
                <div className="grid grid-cols-5 gap-2 mb-3">
                  {presetAmounts.map((amount) => (
                    <Button
                      key={amount}
                      type="button"
                      variant={formData.amount === amount.toString() ? "default" : "outline"}
                      onClick={() => handlePresetAmount(amount)}
                      className="w-full"
                    >
                      £{amount}
                    </Button>
                  ))}
                </div>
                <Input
                  type="number"
                  name="amount"
                  placeholder="Or enter custom amount"
                  value={formData.amount}
                  onChange={handleChange}
                  min="1"
                  step="0.01"
                  required
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="anonymous"
                  name="anonymous"
                  checked={formData.anonymous}
                  onChange={handleChange}
                  className="h-4 w-4"
                />
                <Label htmlFor="anonymous">Make this donation anonymous</Label>
              </div>

              {!formData.anonymous && (
                <>
                  <div>
                    <Label htmlFor="donorName">Your Name (optional)</Label>
                    <Input
                      id="donorName"
                      name="donorName"
                      value={formData.donorName}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="donorEmail">Your Email (optional)</Label>
                    <Input
                      id="donorEmail"
                      name="donorEmail"
                      type="email"
                      value={formData.donorEmail}
                      onChange={handleChange}
                    />
                  </div>
                </>
              )}

              <div>
                <Label htmlFor="message">Message (optional)</Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Leave a message of support..."
                />
              </div>

              <Button type="submit" className="w-full" disabled={loading}>
                {loading ? "Processing..." : `Donate £${formData.amount || "0.00"}`}
              </Button>

              <p className="text-sm text-gray-600 text-center">
                Your donation is processed securely via Stripe. We accept all major credit and debit cards.
              </p>
            </form>
          </CardContent>
        </Card>

        <div className="mt-12 text-center">
          <h2 className="text-2xl font-bold mb-4">Why Your Support Matters</h2>
          <div className="grid md:grid-cols-3 gap-6 mt-8">
            <div>
              <h3 className="font-semibold mb-2">Rescue & Recovery</h3>
              <p className="text-sm text-gray-600">
                We provide immediate care for horses rescued from neglect, abuse, 
                or abandonment.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Rehabilitation</h3>
              <p className="text-sm text-gray-600">
                Our team works tirelessly to restore health and trust through 
                professional care and training.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Rehoming</h3>
              <p className="text-sm text-gray-600">
                We find loving forever homes where horses can thrive and live 
                happy lives.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DonatePage;
